/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { createSelector } from 'reselect';

import { ApiStatus } from '../../../../ts';
import { LaunchModeResponseMode } from '../../../../ts/skill';
import type { RootState } from '../../../../ts/state';
import { textExpressionPiecesToText } from '../../../actions/util/actionUtil';
import { selectLimits } from '../instance/selectors';
import {
  selectSkill,
  selectSkillById,
  selectSkillCount
} from './core-selectors';
import {
  LAUNCH_MODE_LEARNING_DEFAULT,
  LAUNCH_MODE_CONFIDENT_DEFAULT,
  toWorkspace,
  ALGO_VERSION_UPDATE_DATE
} from './helper';

/**
 * Indicates if the API to get the training status is in progress, failed, or idle.
 * @param {object} state the redux store state
 * @param {string} skillId skill id
 * @returns {string} training status is in progress, failed, or idle.
 */
export const selectTrainingCheckStatusForSkillId = (
  state: RootState,
  skillId?: string
): ApiStatus => {
  return state?.skills?.trainingStatus[skillId];
};

export const selectTestingCheckStatusForSkillId = (
  state: RootState,
  skillId?: string
): ApiStatus => {
  return state?.skills?.testingStatus[skillId];
};

/**
 * indicates if the skill is training or not.
 * @param {object} state the redux store state
 * @param {string} skillId skill id
 * @returns {boolean} returns if the skill is training or not.
 */

export const selectTrainingStatusForSkillId = (
  state: RootState,
  skillId?: string
) => state?.skills?.training[skillId];

export const selectTestingStatusForSkillId = (
  state: RootState,
  skillId?: string
) => state?.skills?.testing[skillId];

export const selectTestingStatusIdle = (state: RootState): boolean =>
  state?.skills?.testingStatus === ApiStatus.IDLE;

export const selectTestingStatusLoading = (state: RootState): boolean =>
  state?.skills?.testingStatus === ApiStatus.LOADING;

/**
 * Checks if the skill limit has been reached
 * @param {object} state the redux store state
 * @returns {boolean} returns true if the number of skills equals or exceeds the plan limits
 */
export const isSkillLimitReached = (state: RootState): boolean => {
  const limit = selectLimits(state)?.skills;
  if (!limit) {
    return false;
  }
  const count = selectSkillCount(state);
  return count >= limit;
};

export const selectWorkspaceBySkillId = createSelector(
  [selectSkillById],
  skill => {
    if (!skill || !skill.workspace_id) {
      return null;
    }

    return toWorkspace(skill);
  }
);

export const selectWorkspaceSkill = (state: RootState) =>
  selectWorkspaceBySkillId(state, state?.session?.skillId);

export const selectTopicSwitchSettings = createSelector(selectSkill, skill => {
  if (!skill?.workspace_reference?.system_settings?.topic_switch) {
    return {
      enabled: undefined,
      messages: undefined,
      digression_handler: undefined
    };
  }
  const { topic_switch } = skill.workspace_reference.system_settings;

  const { enable_confirmation, confirmation_request, confirmation_failure } =
    topic_switch?.messages ?? {};

  //format the digression settings.
  const options =
    confirmation_request?.generic?.filter(item => item.options) ?? [];
  const request = confirmation_request?.generic
    ? textExpressionPiecesToText(
        confirmation_request?.generic[0].values[0].text_expression.concat
      )
    : // eslint-disable-next-line no-template-curly-in-string
      'Do you want to return to your previous topic ${digressed_from}?';
  const validMessage = confirmation_failure?.generic
    ? textExpressionPiecesToText(
        confirmation_failure?.generic[0].values[0].text_expression.concat
      )
    : 'Please reply back with yes or no';
  const formattedMessages = {
    enable_confirmation,
    confirmation_request: request,
    confirmation_failure: validMessage,
    confirmation_buttons: options.length > 0
  };
  return {
    ...skill.workspace_reference.system_settings.topic_switch,
    enabled: topic_switch.enabled,
    messages: formattedMessages
  };
});

export const selectDisambiguationSettings = createSelector(
  selectSkill,
  skill => {
    if (!skill?.workspace_reference?.system_settings?.disambiguation) {
      return {};
    }

    const { disambiguation } = skill.workspace_reference.system_settings;
    return {
      enabled: disambiguation.enabled,
      prompt: disambiguation.prompt,
      noneOfAbovePrompt: disambiguation.none_of_the_above_prompt,
      connectToSupportPrompt: disambiguation.use_connect_to_support_prompt,
      singleAnswerClarificationPrompt:
        disambiguation?.single_answer_clarification_prompt
    };
  }
);

export const selectDisplayFormatSettings = createSelector(
  selectSkill,
  skill => {
    if (skill?.workspace_reference?.system_settings) {
      const { variable, default_locale } =
        skill.workspace_reference.system_settings;

      if ((default_locale || default_locale === null) && !variable) {
        return {
          locale: default_locale
        };
      } else if (variable) {
        const { format } = variable;
        return {
          locale: default_locale,
          timeFormat: format?.time?.pattern,
          dateFormat: format?.date?.pattern,
          numberDelimiter: format?.number?.delimiter,
          numberFraction: format?.number?.fraction_digits,
          percentFraction: format?.percent?.fraction_digits,
          currencyFraction: format?.currency?.fraction_digits,
          currencySymbol: format?.currency?.symbol
        };
      }
    }
    return {};
  }
);

export const selectLaunchModeSettings = (state: RootState) => {
  const skill = selectSkillById(state, state?.session?.skillId);

  const launchModeSettings =
    skill?.workspace_reference?.system_settings?.launch_mode;

  // If launch modes is enabled but other properties don't exist we should show default values
  const enabledWithUndefinedDefaults =
    launchModeSettings?.enabled &&
    (!launchModeSettings?.learning || !launchModeSettings?.confident);

  if (!launchModeSettings) {
    return {
      confident: LAUNCH_MODE_CONFIDENT_DEFAULT,
      default: LaunchModeResponseMode.LEARNING,
      enabled: false,
      learning: LAUNCH_MODE_LEARNING_DEFAULT
    };
  } else if (enabledWithUndefinedDefaults) {
    return {
      confident: LAUNCH_MODE_CONFIDENT_DEFAULT,
      default: LaunchModeResponseMode.LEARNING,
      enabled: true,
      learning: LAUNCH_MODE_LEARNING_DEFAULT
    };
  }
  return skill?.workspace_reference?.system_settings?.launch_mode;
};

//returns true when skills are fetched
export const selectSkillsHasBeenFetched = (state: RootState) =>
  !_.isNull(state?.skills?.listFetched);

export const selectIsUpdatingSkill = (state: RootState) =>
  state?.skills?.updateStatus === ApiStatus.LOADING;

export const selectIsCreatingSkill = (state: RootState) =>
  state?.skills?.createStatus === ApiStatus.LOADING;

export const selectAreSkillsLoading = (state: RootState) =>
  state?.skills?.listStatus === ApiStatus.LOADING;

export const selectImportStatus = (state: RootState) =>
  state?.skills.importStatus;

export const selectToggleDialogStatus = (state: RootState) =>
  state?.skills?.toggleDialogStatus;

//get algorithm versions
export const selectSystemSettings = createSelector(selectSkill, skill => {
  return skill?.workspace_reference?.system_settings ?? {};
});

export const selectIsAlgorithmVersonUpdated = createSelector(
  selectSkill,
  skill => {
    return (
      new Date(
        skill?.workspace_reference?.system_settings?.nlp?.versions?.latest
      ) >= new Date(ALGO_VERSION_UPDATE_DATE)
    );
  }
);

export const selectGenerativeAISettings = createSelector(selectSkill, skill => {
  return skill?.workspace_reference?.system_settings?.generative_ai;
});
